import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Flex,
  Spinner,
} from "@chakra-ui/react";
import { colors } from "../../definitions/constants/GlobalStyles";
import i18n from "../../i18n/i18n";
import BodyText from "./BodyText";
import { useEffect, useState } from "react";
import { getNotificationFromUser, setNotificationsToRead } from "../../models/UserModel";
import { TypeNotifications } from "../../definitions/enums/GlobalEnums";
import { appStore } from "../../zustand/globalStore";

function NotificationModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { userData } = appStore((state: any) => state);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const init = async () => {
      if (userData?.id && isOpen) {
        setIsLoading(true);
        try {
          const fetchedNotifications = await getNotificationFromUser(userData.id);
          setNotifications(fetchedNotifications);

          // Mark as read after 3 seconds like in mobile
          timeoutId = setTimeout(() => {
            setNotificationsToRead(userData.id);
          }, 3000);
        } catch (error) {
          console.error("Error fetching notifications", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (isOpen) {
      init();
    } else {
      setNotifications([]);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isOpen, userData]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      isCentered
      motionPreset="slideInBottom"
    >
      <ModalOverlay />
      <ModalContent
        position="fixed"
        bottom={{
          base: "8px",
          md: "12px",
        }}
        right={{
          base: "8px",
          sm: "16px",
          md: "24px",
          lg: "32px",
        }}
        margin={0}
        borderRadius={24}
        boxShadow="lg"
        p="24px 32px 24px 32px"
        gap={4}
        maxH="60vh"
      >
        <ModalHeader p={0}>
          <BodyText fontSize={22} fontWeight="regular" color={colors.text.black}>
            {i18n.t("notifications")}
          </BodyText>
        </ModalHeader>
        <ModalCloseButton position="absolute" top="16px" right="16px" />
        <ModalBody p={0} overflowY="auto">
          {isLoading ? (
            <Flex justify="center" align="center" py={4}>
              <Spinner size="md" color={colors.backgrounds.action_active} />
            </Flex>
          ) : notifications.length === 0 ? (
            <Flex justify="center" align="center" py={4}>
              <BodyText fontWeight="regular" fontSize={14} color={colors.text.lightGray3}>
                {i18n.t("noData")}
              </BodyText>
            </Flex>
          ) : (
            <Box display="flex" flexDirection="column">
              {notifications.map((item: any, index: number) => {
                let textArrayBold: any[] = [];
                let text = "";
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                let showDetail = true;

                switch (item.type) {
                  case TypeNotifications.teamParticipation:
                    text = i18n.t("msgNotifInvitateTournament");
                    textArrayBold = [item.tournament_name, item.team_name];
                    break;
                  case TypeNotifications.managerInvitation:
                    text = i18n.t("msgNotifRequest");
                    textArrayBold = [item.team_name, item.rol];
                    break;
                  case TypeNotifications.playerInvitation:
                    text = i18n.t("msgNotiPlayerRequest");
                    textArrayBold = [item.team_name, item.tournament_name];
                    break;
                  case TypeNotifications.technicalStaff:
                    text = i18n.t("msgNotiRequestTournament");
                    textArrayBold = [item.team_name, item.rol, item.tournament_name];
                    break;
                  case TypeNotifications.message:
                    text = `{0} ${item.text}`;
                    textArrayBold = [item.title];
                    showDetail = false;
                    break;
                  default:
                    text = item.text || "";
                    showDetail = false;
                    break;
                }

                // Parser for {0}, {1} strings logic as in wuamball-app
                const resultElements: any[] = [];
                const splitText = text.split(/\{\d+\}/);
                let numberOfItemsAdded = 0;

                splitText.forEach((part, i) => {
                  resultElements.push(<span key={`text-${i}`}>{part}</span>);
                  if (numberOfItemsAdded < textArrayBold.length) {
                    resultElements.push(
                      <span key={`bold-${i}`} style={{ fontWeight: 600 }}>
                        {textArrayBold[numberOfItemsAdded]}
                      </span>
                    );
                    numberOfItemsAdded++;
                  }
                });

                const isUnread = showDetail || item?.pending_to_read;

                return (
                  <Flex
                    key={index}
                    direction="row"
                    align="center"
                    justify="space-between"
                    p={3}
                    bg={isUnread ? colors.backgrounds.action_active : colors.backgrounds.white}
                    borderBottom="1px solid"
                    borderColor={colors.backgrounds.gray1}
                    cursor="pointer"
                    _hover={{ bg: colors.backgrounds.gray1 }}
                    onClick={() => {
                      onClose();
                      // TODO: Map routes if implementing NotificationsDetailsScreen for Web later
                      if (item.type === TypeNotifications.message) {
                        // navigate(`/chat/${item.origin_user_id}`);
                      }
                    }}
                  >
                    <Box flex={1}>
                      <BodyText
                        fontWeight="regular"
                        fontSize={13}
                        color={colors.text.black}
                        style={{ marginLeft: 4 }}
                      >
                        {resultElements}
                      </BodyText>
                    </Box>
                  </Flex>
                );
              })}
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default NotificationModal;
